import React, { useEffect } from "react";
import { jsPDF } from "jspdf";
import { FORM_ID } from "../../../constants/Forms";
import PersonalSalesRecordFinancialPlans from "./PersonalSalesRecordFinancialPlans";
import PersonalSalesRecordCommissions from "./PersonalSalesRecordCommissions";
import "../../../assets/stylesheets/StartApplication/CAS/PersonalSalesRecord.scss";
import { UtagTracking } from "../../../utils/utag-tracking";
import NavigationButtons from "../../SharedComponents/NavigationButtons";
import { Col, Row } from "react-bootstrap";
import { Button } from "react-blueprint-library";

function formatCurrency(value) {
  if (isNaN(value)) {
    value = 0;
  }
  var rounded = Math.round(value).toFixed(0);
  let nf = Intl.NumberFormat("en-US");
  return "$" + nf.format(rounded);
}

function PersonalSalesRecordCharts(props) {
  useEffect(() => {
    async function fetchData() {
      await props.refreshPSRCharts();
      UtagTracking.sendPageNameView(
        "personalsalesrecordcharts",
        props.candidateId,
        props.agencynumber,
        props.contracttypeid,
      );
    }
    fetchData();
    // eslint-disable-next-line
  }, []);

  const handleShowBackModal = () => {
    props.toggleForm(FORM_ID.PERSONAL_SALES_RECORD);
  };

  const handleNext = () => {
    props.toggleForm(FORM_ID.CCB_INTRO);
  };

  const drawTable = (pdf, startY, headers, rows, colWidths) => {
    const margin = 40;
    const rowHeight = 20;
    const fontSize = 8;
    const headerFontSize = 9;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Calculate column widths if not provided
    if (!colWidths) {
      const tableWidth = pageWidth - margin * 2;
      colWidths = headers.map(() => tableWidth / headers.length);
    }

    let y = startY;

    const checkPageBreak = (needed) => {
      if (y + needed > pageHeight - 40) {
        pdf.addPage();
        y = 40;
      }
    };

    // Draw header row
    checkPageBreak(rowHeight);
    pdf.setFillColor(230, 230, 230);
    let x = margin;
    pdf.rect(
      x,
      y,
      colWidths.reduce((a, b) => a + b, 0),
      rowHeight,
      "F",
    );
    pdf.setFontSize(headerFontSize);
    pdf.setFont("helvetica", "bold");
    headers.forEach((header, i) => {
      pdf.text(header, x + 4, y + 14);
      x += colWidths[i];
    });
    y += rowHeight;

    // Draw data rows
    pdf.setFontSize(fontSize);
    rows.forEach((row) => {
      checkPageBreak(rowHeight);
      const isBold = row._bold;
      const isGray = row._gray;
      const cells = row._bold || row._gray ? row.cells : row;

      if (isGray) {
        pdf.setFillColor(240, 240, 240);
        pdf.rect(
          margin,
          y,
          colWidths.reduce((a, b) => a + b, 0),
          rowHeight,
          "F",
        );
      }

      pdf.setFont("helvetica", isBold ? "bold" : "normal");
      x = margin;
      cells.forEach((cell, i) => {
        pdf.text(String(cell ?? ""), x + 4, y + 14);
        x += colWidths[i];
      });

      // Draw row bottom border
      pdf.setDrawColor(220, 220, 220);
      pdf.line(
        margin,
        y + rowHeight,
        margin + colWidths.reduce((a, b) => a + b, 0),
        y + rowHeight,
      );
      y += rowHeight;
    });

    return y;
  };

  const exportPDF = () => {
    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "letter",
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 40;
    let y = 40;

    // Title
    pdf.setFontSize(16);
    pdf.setFont("helvetica", "bold");
    const title =
      props.hasNYRegs === false
        ? "Personal Sales Record"
        : "Personal Production Record";
    pdf.text(title, margin, y);
    y += 30;

    // --- Financial Plans Table ---
    if (props.psrDataLength > 0 && props.totalFinPlans !== 0) {
      const finData = Array.from(props.psrFinData);
      const headers = ["Financial Plans"];
      finData.forEach((item, idx) => {
        headers.push(
          idx === 0 ? `Current Year ${item[0]}` : `Year ${idx} (${item[0]})`,
        );
      });

      const tableWidth = pageWidth - margin * 2;
      const colWidths = headers.map((_, i) =>
        i === 0
          ? tableWidth * 0.25
          : (tableWidth * 0.75) / (headers.length - 1),
      );

      pdf.setFontSize(11);
      pdf.setFont("helvetica", "bold");
      pdf.text(
        `Financial Plans: ${props.psrChartData.totalFinPlans}`,
        margin,
        y,
      );
      y += 15;

      const row = ["Number Sold"];
      finData.forEach((item) => {
        row.push(String(item[1] === null ? 0 : item[1]));
      });

      y = drawTable(pdf, y, headers, [row], colWidths);
      y += 25;
    }

    // --- Commissions / Premiums Table ---
    if (props.psrDataLength > 0) {
      const comData = Array.from(props.psrComData);
      const isNY = props.hasNYRegs;
      const label = isNY ? "Premiums" : "Commissions";

      const headers = [""];
      comData.forEach((item, idx) => {
        headers.push(
          idx === 0
            ? `Current Year ${item[0].year}`
            : `Year ${idx} (${item[0].year})`,
        );
      });

      const tableWidth = pageWidth - margin * 2;
      const colWidths = headers.map((_, i) =>
        i === 0
          ? tableWidth * 0.28
          : (tableWidth * 0.72) / (headers.length - 1),
      );

      pdf.setFontSize(11);
      pdf.setFont("helvetica", "bold");
      pdf.text(
        `${label}: ${formatCurrency(isNY ? props.psrChartData.totalPremiums : props.psrChartData.totalComissions)}`,
        margin,
        y,
      );
      y += 15;

      const rows = [];

      if (props.has_ind_life) {
        rows.push({
          _bold: true,
          cells: ["Individual Life Insurance", ...comData.map(() => "")],
        });
        rows.push([
          isNY ? "First Year Premium" : "First Year Commissions",
          ...comData.map((_, k) =>
            formatCurrency(comData[k][0].indlifirstyear),
          ),
        ]);
        if (!isNY) {
          rows.push([
            "Renewal Commissions",
            ...comData.map((_, k) =>
              formatCurrency(comData[k][0].indlirenewcom),
            ),
          ]);
        }
      }

      if (props.has_ind_annuity) {
        rows.push({
          _bold: true,
          cells: ["Individual Annuity", ...comData.map(() => "")],
        });
        rows.push([
          isNY ? "First Year Premium" : "First Year Commissions",
          ...comData.map((_, k) =>
            formatCurrency(comData[k][0].indannfirstyear),
          ),
        ]);
        if (!isNY) {
          rows.push([
            "Renewal Commissions",
            ...comData.map((_, k) =>
              formatCurrency(comData[k][0].indannrenewcom),
            ),
          ]);
        }
      }

      if (props.has_ind_disabilty) {
        rows.push({
          _bold: true,
          cells: ["Individual Disability Income", ...comData.map(() => "")],
        });
        rows.push([
          isNY ? "First Year Premium" : "First Year Commissions",
          ...comData.map((_, k) =>
            formatCurrency(comData[k][0].inddisincfirstyear),
          ),
        ]);
        if (!isNY) {
          rows.push([
            "Renewal Commissions",
            ...comData.map((_, k) =>
              formatCurrency(comData[k][0].inddisincrenewcom),
            ),
          ]);
        }
      }

      if (props.has_ind_long_term) {
        rows.push({
          _bold: true,
          cells: ["Individual Long Term Care", ...comData.map(() => "")],
        });
        rows.push([
          isNY ? "First Year Premium" : "First Year Commissions",
          ...comData.map((_, k) =>
            formatCurrency(comData[k][0].indlongtermfirstyear),
          ),
        ]);
        if (!isNY) {
          rows.push([
            "Renewal Commissions",
            ...comData.map((_, k) =>
              formatCurrency(comData[k][0].indlongtermrenewcom),
            ),
          ]);
        }
      }

      if (props.has_gross_commissions && !isNY) {
        rows.push([
          "Investment Product Gross Dealer Commissions",
          ...comData.map((_, k) =>
            formatCurrency(comData[k][0].invprodgrossdealercom),
          ),
        ]);
      }

      if (props.has_fee_based_sales && !isNY) {
        rows.push([
          "Fee-based Sales Relevant Fees",
          ...comData.map((_, k) =>
            formatCurrency(comData[k][0].feebasedsalesrelfees),
          ),
        ]);
      }

      // Totals row
      rows.push({
        _bold: true,
        _gray: true,
        cells: [
          "Totals",
          ...comData.map((_, k) =>
            formatCurrency(
              isNY === false
                ? comData[k][0].totalPerYear
                : comData[k][0].totalPremiumsPerYear,
            ),
          ),
        ],
      });

      y = drawTable(pdf, y, headers, rows, colWidths);
    }

    const pdfName =
      props.hasNYRegs === false
        ? "Personal_Sales_Record.pdf"
        : "Personal_Production_Record.pdf";
    pdf.save(pdfName);
  };

  return (
    <>
      <div className="row">
        <div className="col-md-12">
          {props.hasNYRegs === false ? (
            <h1>Personal Sales Record</h1>
          ) : (
            <h1>Personal Production Record</h1>
          )}
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <p id="psr_intro_text_tally_screen">
            <>
              Please review the data captured from your{" "}
              {props.hasNYRegs === false ? <>PSRs.</> : <>PPRs.</>} If you need
              to make any changes to the following totals listed below, please
              return to the{" "}
              <span
                id="psr_return_to_emp_hist"
                onClick={handleShowBackModal}
                style={{
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                previous step
              </span>
              .
            </>
          </p>
        </div>
      </div>

      <Row id="printBtnRow">
        <Col lg={9}></Col>
        <Col sm={12} lg={3} className="ml-auto">
          <div className="float-right">
            <Button variant={"primary"} onClick={exportPDF}>
              EXPORT PDF
            </Button>
          </div>
        </Col>
      </Row>

      {props.psrDataLength > 0 && props.totalFinPlans !== 0 && (
        <PersonalSalesRecordFinancialPlans
          data={props.psrChartData}
          financialData={props.psrFinData}
        />
      )}

      {props.psrDataLength > 0 && (
        <div className="psr-chart-wrapper">
          <PersonalSalesRecordCommissions
            data={props.psrChartData}
            comData={props.psrComData}
            hasNYRegs={props.hasNYRegs}
            has_ind_life={props.has_ind_life}
            has_ind_annuity={props.has_ind_annuity}
            has_ind_disabilty={props.has_ind_disabilty}
            has_ind_long_term={props.has_ind_long_term}
            has_gross_commissions={props.has_gross_commissions}
            has_fee_based_sales={props.has_fee_based_sales}
          />
        </div>
      )}

      <div style={{ paddingTop: "50px" }}>
        <NavigationButtons
          onBack={handleShowBackModal}
          onNext={handleNext}
          isLoading={false}
          nxtbtnid={"psr_next_button_two"}
          bckbtnid={"psr_back_button_two"}
        />
      </div>
    </>
  );
}

export default PersonalSalesRecordCharts;
